/**
 * Default progress content for tasks without a specialized progress flow.
 * Shows a contact method select, notes textarea, and optional follow-up date.
 * @agentic connections
 */

import { Stack, Text, Textarea, Button, Alert, Select } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { COMMUNICATION_MEDIUM_SELECT_OPTIONS } from "@benchmark/core/constants";
import type { ProgressContentProps } from "@/types/domain/progress-log";
import type { ProgressContactMethod } from "@benchmark/core/types";
import { CareTimeInlineFields } from "@/components/domain/care-time/CareTimeInlineFields";

/**
 * Fallback progress form for activities without a registered progress content.
 * @agentic connections
 */
export const DefaultProgressContent = ({
  onSubmit,
  onCancel,
  onDirtyChange,
}: ProgressContentProps) => {
  const [contactMethod, setContactMethod] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [minutesLogged, setMinutesLogged] = useState<number | undefined>(
    undefined,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDirty =
    !!contactMethod || notes.trim() !== "" || (minutesLogged ?? 0) > 0;
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        contactMethod: (contactMethod as ProgressContactMethod) ?? undefined,
        outcome: "general-progress",
        outcomeDisplay: "Progress Logged",
        notes: notes || undefined,
        snoozeBehavior: { type: "prompt" },
        minutesLogged:
          minutesLogged && minutesLogged > 0 ? minutesLogged : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to log progress");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Text size="sm">Log a progress update for this activity.</Text>

      <Select
        label="Contact Method (optional)"
        placeholder="Select method..."
        data={COMMUNICATION_MEDIUM_SELECT_OPTIONS}
        value={contactMethod}
        onChange={setContactMethod}
        clearable
      />

      <Textarea
        label="Notes"
        placeholder="Describe the interaction..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        autosize
        minRows={2}
        maxRows={4}
      />

      <CareTimeInlineFields
        minutes={minutesLogged}
        onMinutesChange={setMinutesLogged}
        notes=""
        onNotesChange={() => {
          // Notes captured in the main progress notes field above.
        }}
        label="Care time (optional)"
        helperText="Minutes spent on this activity; written as a care-time entry."
        showNotes={false}
      />

      <Stack gap="xs">
        <Button
          onClick={handleSubmit}
          loading={submitting}
          color="purple"
          fullWidth
        >
          Log Progress
        </Button>
        <Button
          variant="subtle"
          onClick={onCancel}
          disabled={submitting}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
